/**
 * Re-percent-encodes query parameter keys/values in a path+search string.
 *
 * `URLSearchParams.get("redirect_uri")` (and similar) decode the redirect target,
 * turning `%2B` → `+` and `%2F` → `/`. Assigning that decoded string to
 * `window.location.href` leaves those characters literal. Form-urlencoded parsers
 * (including `URLSearchParams` / TanStack Router) then treat `+` as a space,
 * corrupting opaque tokens such as Cars `vehicleId`.
 *
 * Decode-then-encode is idempotent for both already-decoded (`+`) and still-encoded
 * (`%2B`) values, and uses `decodeURIComponent` (not form-urlencoded rules) so a
 * literal `+` stays a plus.
 */
export function encodePathQueryValues(pathWithSearch: string): string {
  const hashIndex = pathWithSearch.indexOf("#");
  const hash = hashIndex === -1 ? "" : pathWithSearch.slice(hashIndex);
  const withoutHash =
    hashIndex === -1 ? pathWithSearch : pathWithSearch.slice(0, hashIndex);

  const queryIndex = withoutHash.indexOf("?");
  if (queryIndex === -1) {
    return pathWithSearch;
  }

  const path = withoutHash.slice(0, queryIndex);
  const query = withoutHash.slice(queryIndex + 1);

  const encodedQuery = query
    .split("&")
    .map((pair) => {
      if (pair === "") {
        return pair;
      }
      const eqIndex = pair.indexOf("=");
      if (eqIndex === -1) {
        return encodeURIComponent(safeDecodeURIComponent(pair));
      }
      const key = pair.slice(0, eqIndex);
      const value = pair.slice(eqIndex + 1);
      return `${encodeURIComponent(safeDecodeURIComponent(key))}=${encodeURIComponent(
        safeDecodeURIComponent(value)
      )}`;
    })
    .join("&");

  return `${path}?${encodedQuery}${hash}`;
}

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
